/* eslint-disable react/no-unused-prop-types */
import React from 'react'
import Rx from 'rx'
import styled from '@emotion/styled'
import { State, hasLoadedWithoutError, Loading, Errored } from 'rwwa-data-access'
import { CountdownBadge } from '@mobi/component-library/Feedback/CountdownBadge'
import { observe } from '@core/Components/HOCs/observe'
import { GiddyUpRaces, IRaceRecord, MeetingCode } from '../../data-access/giddyUpRaces'
import { GiddyUpQuaddieTips, IQuaddieRecord } from '../../data-access/giddyUpQuaddieTips'
import { IViewState, ViewType, page$ } from '../../states/page'
import { ShowTopRating, ShowAllRating, ShowSpeedMap, ShowQuaddieTips } from '../../states/signals'
import { getRaceDetail } from '../../integration'
import type { IRaceDetail } from '../../types'
import { AllRating } from './Components/AllRating'
import { TopRating } from './Components/TopRating'
import { QuaddieTips } from './Components/QuaddieTips'
import { SpeedMap } from './Components/SpeedMap'
import { DogSpeedMap } from './Components/DogSpeedMap'

interface GiddyUpState {
  timer: number
  page: IViewState
  raceState: State<IRaceRecord>
  quaddieState: State<IQuaddieRecord>
}

interface GiddyUpProps {
  raceKey: string
  fixtureKey: string
}

const TabbedContent: React.FC<GiddyUpState & GiddyUpProps> = ({
  fixtureKey,
  quaddieState,
  raceKey,
  page,
  raceState,
}) => {
  const raceDetail = getRaceDetail()
  const quaddie = fixtureKey && GiddyUpQuaddieTips.getFrom(quaddieState, fixtureKey)
  const displayQuaddieTab = shouldDisplayQuaddieTab(quaddie, raceKey)

  return (
    <div>
      <div
        style={{
          maxWidth: '64em',
          margin: '0 auto',
          fontSize: '1.2em',
        }}
      >
        {raceDetail && (
          <div className='page-heading' data-tid-race-details>
            <div className='l-faux-table l-table-fixed w100'>
              <div
                className='l-faux-cell alignmid text-center w100 pt1em pbhalfem fz-16 faux-strong'
                data-tid-race-course-and-number
              >
                {(raceDetail.raceCourse || '').toUpperCase()}: R{raceDetail.raceNumber}
              </div>
            </div>

            <div
              className='l-faux-table w100'
              style={{ paddingTop: '0.5em', paddingBottom: '0.5em' }}
            >
              <div className='l-faux-cell l-cell-xs-6 alignmid plhalfem'>
                {isRaceOpen(raceDetail) ? (
                  <CountdownBadge advertisedStartTime={raceDetail.raceTimeLocal} badgeSize='L' />
                ) : (
                  <div className='fz-14 faux-strong' style={{ color: '#f05052' }}>
                    {getRaceStatusDisplay(raceDetail.raceStatus) +
                      (raceDetail.raceResults ? ` (${raceDetail.raceResults})` : '')}
                  </div>
                )}
              </div>

              <div className='l-faux-cell l-cell-xs-1 text-center fz-12'>
                {raceDetail.weatherClass && (
                  <span
                    className={`prhalfem tticon fz-18 alignmid ${raceDetail.weatherClass}`}
                    data-tid-weather-class
                  />
                )}
              </div>

              <div className='l-faux-cell l-cell-xs-3 text-center fz-12'>
                <div className='dib' data-tid-track-condition>
                  {(raceDetail.trackCondition || '').toUpperCase()}
                </div>
              </div>

              <div className='l-faux-cell l-cell-xs-2 alignmid fz-12 text-center' data-tid-distance>
                {raceDetail.distance}m
              </div>
            </div>
          </div>
        )}

        <div className='l-faux-table w100'>
          <div className='l-faux-row w100'>
            <Tab
              onClick={ShowTopRating}
              title='Top Rating'
              active={page.currentView === ViewType.TopRating}
            />
            <Tab
              onClick={ShowSpeedMap}
              title='Speed Map'
              active={page.currentView === ViewType.SpeedMap}
            />
            <Tab
              onClick={ShowAllRating}
              title='All Ratings'
              active={page.currentView === ViewType.AllRating}
            />
            {displayQuaddieTab && (
              <Tab
                onClick={ShowQuaddieTips}
                title='Quaddie Tips'
                active={page.currentView === ViewType.QuaddieTips}
              />
            )}
          </div>
        </div>

        {(() => {
          switch (page.currentView) {
            case ViewType.TopRating:
              return <TopRating raceKey={raceKey} />
            case ViewType.AllRating:
              return <AllRating raceKey={raceKey} />
            case ViewType.QuaddieTips:
              return <QuaddieTips fixtureKey={fixtureKey} />
            case ViewType.SpeedMap: {
              const race = GiddyUpRaces.getFrom(raceState, raceKey)

              if (!hasLoadedWithoutError(race)) {
                return <div />
              }

              return race.meetingCode !== MeetingCode.Dogs ? (
                <SpeedMap raceKey={raceKey} />
              ) : (
                <DogSpeedMap raceKey={raceKey} />
              )
            }
            default:
              return <div />
          }
        })()}
      </div>
    </div>
  )
}

const componentState$ = Rx.Observable.timer(0, 1000).combineLatest(
  page$,
  GiddyUpRaces.data$,
  GiddyUpQuaddieTips.data$,
  (timer, page, raceState, quaddieState) =>
    ({ timer, page, raceState, quaddieState }) as GiddyUpState
)

export const GiddyUp = observe<GiddyUpState, GiddyUpProps>(
  componentState$,
  ({ raceKey, fixtureKey, ...appState }) => {
    return <TabbedContent raceKey={raceKey} fixtureKey={fixtureKey} {...appState} />
  }
)

// ===============
// Local Component
// ===============

const Tab = ({
  title,
  onClick,
  active,
}: {
  title: string
  onClick: () => void
  active: boolean
}) => {
  const activeInactiveStyle = active ? 'giddyupTabActive' : 'giddyupTabInactive'
  const containerAttributes = {
    'data-tid-navigation-tab':
      (active ? 'active ' : 'inactive ') + title.toLowerCase().replace(' ', '-'),
    onClick: active ? () => null : onClick,
  }

  return (
    <GiddyUpTabStyled
      {...containerAttributes}
      className={`l-faux-cell fz-12 text-center ${activeInactiveStyle}`}
    >
      <span>{title}</span>
    </GiddyUpTabStyled>
  )
}

// =============
// Local Helpers
// =============

function getRaceStatusDisplay(raceStatus: string) {
  switch (raceStatus) {
    case 'Released':
      return 'Results'
    case 'Closed':
      return 'Betting Closed'
    default:
      return raceStatus
  }
}

function shouldDisplayQuaddieTab(
  quaddie: '' | IQuaddieRecord | Loading | Errored,
  currentRaceKey: string
): boolean {
  return (
    !!quaddie &&
    hasLoadedWithoutError(quaddie) &&
    quaddie.legs.count() > 0 &&
    quaddie.legs.first().raceKey === currentRaceKey
  )
}

function isRaceOpen(raceDetail: IRaceDetail | null) {
  return raceDetail ? raceDetail.raceStatus === 'Open' : true
}

// ======
// Styles
// ======

const GiddyUpTabStyled = styled.div({
  paddingTop: '0.85em',
  paddingBottom: '0.85em',

  '&.giddyupTabActive': {
    background: '#fff',
    color: '#333',
  },

  '&.giddyupTabInactive': {
    background: '#713d90',
    color: '#fff',
    borderRight: '1px solid #552d6c',
    borderLeft: '1px solid #552d6c',
  },
})
